import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // 🔒 prevents duplicate profile loads (StrictMode / remount safe)
  const didLoadRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      didLoadRef.current = false;
      return;
    }

    // ⛔ load profile ONLY ONCE per token lifecycle
    if (didLoadRef.current) return;
    didLoadRef.current = true;

    const loadUser = async () => {
      try {
        const res = await axios.get(`${API}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUser((prev) => {
          // Always accept the server's data if there is no previous state.
          if (!prev) return res.data;

          // ── Race-condition guard (narrow) ─────────────────────────────
          // Problem this solves: after a successful payment the WebSocket
          // sets role:"premium" locally, but a profile reload that was
          // already in-flight may return the old role:"general" because
          // the DB write hasn't propagated yet to the read replica.
          //
          // Old (broken) fix: block ALL downgrades from premium → anything.
          // This also blocked *legitimate* downgrades (expired subscription)
          // and left users seeing a premium UI they no longer have access to.
          //
          // Correct fix: only hold local premium state when the server data
          // has a valid future expiry that we already know about.  If the
          // server says premium_expires_at is null or in the past, the
          // subscription has genuinely expired and we must accept the downgrade.
          const serverExpiry = res.data.premium_expires_at
            ? new Date(res.data.premium_expires_at)
            : null;
          const serverHasActivePremium =
            serverExpiry && serverExpiry > new Date();

          if (prev.role === "premium" && res.data.role !== "premium") {
            // Only suppress the downgrade if the server still shows an
            // active future expiry — this means it was a stale read, not
            // a genuine expiry.
            if (serverHasActivePremium) {
              return prev;
            }
            // Server says premium has expired — accept the downgrade.
          }

          return res.data;
        });
      } catch (err) {
        console.error("Auth loadUser failed:", err);
        logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [token]);

  // ✅ login = authoritative bootstrap
  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    didLoadRef.current = false; // reset for new token
    setToken(newToken);
    setUser(userData);
  };

  // 🚪 logout clears everything
  const logout = () => {
    localStorage.removeItem("token");
    didLoadRef.current = false;
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser, // 🔑 REQUIRED for WebSocket updates
        token,
        login,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);